import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [organizationId, setOrganizationId] = useState(null);
  const [orgName, setOrgName] = useState('');
  // Developer-section gate: demo/prospect memberships have this switched off.
  const [developerAccess, setDeveloperAccess] = useState(true);
  // TutorTracer staff (tutortracer_admins). Gates the internal Developer section;
  // customers manage API keys from the API page instead.
  // null = still checking, so staff-only routes wait instead of redirecting.
  const [isStaff, setIsStaff] = useState(null);

  const loadOrganization = async (userId) => {
    supabase.from('tutortracer_admins').select('user_id').eq('user_id', userId).maybeSingle()
      .then(({ data }) => setIsStaff(!!data), () => setIsStaff(false));
    try {
      const { data } = await supabase
        .from('user_organizations')
        .select('organization_id, developer_access')
        .eq('user_id', userId)
        .single();
      if (data) {
        setOrganizationId(data.organization_id);
        setDeveloperAccess(data.developer_access !== false);
        const { data: orgData } = await supabase
          .from('organizations')
          .select('name')
          .eq('id', data.organization_id)
          .single();
        if (orgData) setOrgName(orgData.name);
      }
    } catch (e) {
      console.error('Error loading org:', e);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) loadOrganization(session.user.id);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadOrganization(session.user.id);
      } else {
        setOrganizationId(null);
        setOrgName('');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data.user;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setOrganizationId(null);
    setOrgName('');
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, organizationId, orgName, developerAccess, isStaff }}>
      {children}
    </AuthContext.Provider>
  );
};
